/**
 * Self-hosted API client (migration Part 9).
 *
 * Drop-in replacement for the Sharetribe Flex SDK instance the SPA consumes via
 * the thunk `sdk` extra-arg. `createInstance()` returns an object whose method
 * surface mirrors the Flex SDK (`sdk.listings.query`, `sdk.currentUser.show`,
 * `sdk.transactions.show`, `sdk.login`, ...) so ducks change only their
 * transport, not their response handling.
 *
 * Response envelope matches the SDK: methods resolve to `{ data, status,
 * statusText }` where `data` is the endpoint body `{ data, included, meta }`, so
 * `denormalisedResponseEntities(response)` (reads `response.data.data` +
 * `.included`) keeps working. The Part 4 serializers already emit the Flex
 * JSON:API deserialized shape.
 *
 * Migration status: methods backed by a built endpoint (Parts 4/5/6) hit `/api`.
 * Everything not yet built REJECTS with `NotMigratedError` (async, so app boot
 * continues and only the specific page errors) - see the `notMigrated` list.
 * As each server endpoint + page lands, its method moves from `notMigrated` to a
 * real implementation.
 *
 * `p9-drop-final`: the app speaks JSON end-to-end, so the former Flex
 * `transit`/`util` re-exports are gone and `sharetribe-flex-sdk` is uninstalled.
 * Value `types` come from the dependency-free `./sdkTypes`.
 */

import * as sdkTypes from './sdkTypes';

export const types = sdkTypes.types;

export class NotMigratedError extends Error {
  constructor(method) {
    super(`[apiClient] '${method}' is not migrated to the self-hosted API yet (Part 9).`);
    this.name = 'NotMigratedError';
    this.status = 501;
    this.statusText = 'Not Implemented';
    this.data = { errors: [{ status: 501, code: 'not-migrated', title: this.message }] };
  }
}

// Base URL resolution (mirrors src/util/api.js; kept local to avoid an import
// cycle with sdkLoader).
const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;
  if (useDevApiServer) return `http://localhost:${port}`;
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
};

const buildQuery = params => {
  if (!params || typeof params !== 'object') return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      if (value.length) usp.set(key, value.join(','));
    } else if (value instanceof sdkTypes.UUID) {
      usp.set(key, value.uuid);
    } else if (typeof value === 'object') {
      // e.g. imageVariant.* config objects - not honored by the self-hosted
      // image contract (Part 3 uses fixed variant names); skip quietly.
    } else {
      usp.set(key, String(value));
    }
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
};

// Map self-hosted server error codes / HTTP status to the Flex error codes that
// src/util/errors.js predicates expect (values from src/util/types.js). The raw
// server code is preserved in `meta.serverCode`.
const SERVER_CODE_TO_FLEX = {
  not_found: 'not-found',
  forbidden: 'forbidden',
  email_taken: 'email-taken',
  email_not_found: 'email-not-found',
  invalid_transition: 'transaction-invalid-transition',
  missing_stripe_account: 'transaction-missing-stripe-account',
  old_total_mismatch: 'old-total-mismatch',
  payment_failed: 'transaction-payment-failed',
};
const STATUS_TO_FLEX = { 403: 'forbidden', 404: 'not-found' };

const flexCode = (status, serverCode) =>
  SERVER_CODE_TO_FLEX[serverCode] || STATUS_TO_FLEX[status] || serverCode || 'api-error';

// Normalize a failed response into the Flex error shape so src/util/errors.js
// predicates work: `error.status` + `error.data.errors = [{ status, code, meta }]`.
const toError = (status, statusText, body) => {
  const serverCode = body?.error || body?.code || null;
  const err = new Error(body?.message || statusText || 'API request failed');
  err.status = status;
  err.statusText = body?.message || statusText;
  err.data = {
    errors: [
      {
        status,
        code: flexCode(status, serverCode),
        title: body?.message || statusText,
        meta: { serverCode, ...(body?.meta || {}) },
      },
    ],
  };
  return err;
};

// Resolve a fetch implementation for the current context. Browser: window.fetch.
// Server (SSR): the global fetch (Node >= 18) - createInstance also injects a
// baseUrl + Cookie so the SSR sdk hits our own /api in-process.
const resolveFetch = configFetch => {
  if (configFetch) return configFetch;
  if (typeof window !== 'undefined' && window.fetch) return window.fetch.bind(window);
  if (typeof globalThis !== 'undefined' && globalThis.fetch)
    return globalThis.fetch.bind(globalThis);
  return undefined;
};

// Build the transport bound to one createInstance() context. On the browser the
// session rides on the cookie (credentials:'include'); on the server we forward
// the incoming request's Cookie header explicitly.
const makeRequest = ({ fetchImpl, baseUrl, cookie, extraHeaders }) => async (
  path,
  { method = 'GET', params, body } = {}
) => {
  const url = `${baseUrl}${path}${method === 'GET' ? buildQuery(params) : ''}`;
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (cookie) headers.Cookie = cookie;
  // SSR loopback calls (baseUrl = http://127.0.0.1) run behind the proxy's TLS
  // termination; forward the original proto so express-enforces-ssl doesn't
  // 301-redirect the in-process request to https (which would TLS-handshake a
  // plaintext socket -> ERR_SSL_WRONG_VERSION_NUMBER).
  if (extraHeaders) Object.assign(headers, extraHeaders);
  const opts = { method, credentials: 'include', headers };
  if (body != null && method !== 'GET') opts.body = JSON.stringify(body);

  const res = await fetchImpl(url, opts);
  const contentType = (res.headers.get('Content-Type') || '').split(';')[0];
  // Parse JSON with the sdkTypes reviver so server-emitted value tags
  // ({_sdkType:'Money'|'UUID'|'LatLng'|...}) are reconstructed into real class
  // instances - mirrors the Flex SDK transit reader, so formatMoney/UUID checks
  // and instanceof throughout the SPA keep working.
  const parse = () =>
    res.text().then(t => {
      if (!t) return {};
      if (contentType === 'application/json') return JSON.parse(t, sdkTypes.reviver);
      return { message: t };
    });

  if (res.status >= 400) {
    const errBody = await parse().catch(() => ({}));
    throw toError(res.status, res.statusText, errBody);
  }
  const data = await parse();
  return { data, status: res.status, statusText: res.statusText };
};

const reject = method => () => Promise.reject(new NotMigratedError(method));

// Resources not yet backed by a server endpoint. Each rejects (async) so boot
// survives; move them to real methods as their endpoint + page is migrated.
const notMigrated = {
  timeslots: ['query'],
  stripeAccountLinks: ['create'],
  stripeSetupIntents: ['create'],
  stripeCustomer: ['create', 'addPaymentMethod', 'deletePaymentMethod'],
  stripePersonTokens: ['create'],
  processTransitions: ['query'],
};

// Upload raw image bytes to the Part 3 storage endpoint. The SDK signature is
// `images.upload({ image: File }, queryParams)`; the file is sent as the raw
// body (Content-Type = the file's MIME) - NOT JSON. Returns the SDK envelope
// `{ data: { data: { id, type, attributes } } }` so `resp.data.data` is the image.
// (Browser-only; SSR never uploads.)
const makeUploadImage = ({ fetchImpl, baseUrl, cookie }) => async ({ image } = {}, queryParams) => {
  if (!image) throw new NotMigratedError('images.upload (no file)');
  const listingId =
    queryParams?.listingId?.uuid || queryParams?.listingId || queryParams?.listing_id || null;
  const qs = listingId ? `?listingId=${encodeURIComponent(listingId)}` : '';
  const url = `${baseUrl}/api/listing-images/upload${qs}`;
  const headers = { 'Content-Type': image.type || 'application/octet-stream' };
  if (cookie) headers.Cookie = cookie;
  const res = await fetchImpl(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: image,
  });
  const text = await res.text().catch(() => '');
  let body = {};
  if (text) {
    try {
      body = JSON.parse(text, sdkTypes.reviver);
    } catch (e) {
      body = { message: text };
    }
  }
  if (res.status >= 400) throw toError(res.status, res.statusText, body);
  if (!body?.data) {
    throw toError(502, 'Bad Gateway', { message: 'Image upload returned an empty payload.' });
  }
  return { data: body, status: res.status, statusText: res.statusText };
};

const buildNotMigrated = () => {
  const out = {};
  Object.entries(notMigrated).forEach(([resource, methods]) => {
    out[resource] = {};
    methods.forEach(m => {
      out[resource][m] = reject(`${resource}.${m}`);
    });
  });
  return out;
};

export const createInstance = (config = {}) => {
  // Transport context. Browser: window.fetch + relative/dev base + cookie session.
  // Server (SSR): pass { fetchImpl?, baseUrl, cookie } so this same client hits
  // our own /api in-process with the end-user's forwarded session cookie.
  const ctx = {
    fetchImpl: resolveFetch(config.fetchImpl),
    baseUrl: config.baseUrl != null ? config.baseUrl : apiBaseUrl(),
    cookie: config.cookie || null,
    extraHeaders: config.headers || null,
  };
  const request = makeRequest(ctx);
  const uploadImage = makeUploadImage(ctx);

  const sdk = {
    _config: config,
    _selfHosted: true,

    // ---- Reads (Part 4) ----
    listings: {
      query: params => request('/api/listings/query', { params }),
      show: params => request('/api/listings/show', { params }),
    },
    users: {
      show: params => request('/api/users/show', { params }),
      query: params => request('/api/users/query', { params }),
      adminUpdate: params =>
        request('/api/admin/users/update', { method: 'POST', body: { params } }),
      adminDelete: params =>
        request('/api/admin/users/delete', { method: 'POST', body: { params } }),
    },

    // ---- Own listings (Part 9 writes) ----
    // query/show read; create-draft/update/publish/open/close mutate. All
    // owner-scoped + session-authenticated. queryParams (include/expand/fields)
    // are ignored: the endpoints always hydrate author+images+currentStock.
    // `create`/`addImage` are unused by the SPA (drafts use createDraft; images
    // attach via the `images:[uuid]` array on createDraft/update).
    ownListings: {
      query: params => request('/api/own-listings/query', { params }),
      show: params => request('/api/own-listings/show', { params: { id: params?.id } }),
      create: params =>
        request('/api/own-listings/create-draft', { method: 'POST', body: { params } }),
      createDraft: params =>
        request('/api/own-listings/create-draft', { method: 'POST', body: { params } }),
      update: params => request('/api/own-listings/update', { method: 'POST', body: { params } }),
      open: params => request('/api/own-listings/open', { method: 'POST', body: { params } }),
      close: params => request('/api/own-listings/close', { method: 'POST', body: { params } }),
      publishDraft: params =>
        request('/api/own-listings/publish', { method: 'POST', body: { params } }),
      addImage: reject('ownListings.addImage'),
    },
    stock: {
      compareAndSet: params =>
        request('/api/stock/compare-and-set', { method: 'POST', body: { params } }),
    },
    // ---- Messages + reviews (Part 9 post-purchase) ----
    // messages: participant-only per transaction. reviews: public, filtered by
    // subject_id (profile) or listing_id (listing). Review CREATION happens via
    // the review transaction transition (transactions.transition), not here.
    messages: {
      query: params => request('/api/messages/query', { params }),
      send: params => request('/api/messages/send', { method: 'POST', body: { params } }),
    },
    reviews: {
      query: params => request('/api/reviews/query', { params }),
    },
    images: {
      upload: uploadImage,
    },
    stripeAccount: {
      fetch: () => request('/api/stripe-account'),
      create: reject('stripeAccount.create'),
      update: reject('stripeAccount.update'),
    },
    // Booking availability: this marketplace is stock/product-based, so there
    // are no exceptions. `query` resolves empty (keeps EditListingPage.loadData
    // green); create/delete stay unmigrated (no booking listing types).
    availabilityExceptions: {
      query: () =>
        Promise.resolve({
          data: { data: [], meta: { totalItems: 0, totalPages: 0, page: 1, perPage: 100 } },
          status: 200,
          statusText: 'OK',
        }),
      create: reject('availabilityExceptions.create'),
      delete: reject('availabilityExceptions.delete'),
    },
    currentUser: {
      show: () => request('/api/me'),
      // Signup (Part 5): /api/auth/local/signup creates the user AND issues the
      // session; auth.duck's follow-up login() is then a no-op re-auth. Extra
      // profile keys (publicData/protectedData) are passed through for
      // forward-compat; the endpoint currently reads email/password/name only.
      create: params => request('/api/auth/local/signup', { method: 'POST', body: params }),
      // Profile / account mutations (Part 9 p9-writes). queryParams
      // (expand/include/fields.image) are ignored; the endpoint hydrates
      // profileImage into `included` itself.
      updateProfile: params =>
        request('/api/current-user/update-profile', { method: 'POST', body: { params } }),
      changeEmail: params =>
        request('/api/current-user/change-email', { method: 'POST', body: { params } }),
      changePassword: params =>
        request('/api/current-user/change-password', { method: 'POST', body: { params } }),
      sendVerificationEmail: () =>
        request('/api/current-user/send-verification-email', { method: 'POST', body: {} }),
      verifyEmail: params =>
        request('/api/current-user/verify-email', { method: 'POST', body: { params } }),
    },

    // ---- Transactions (Part 6 reads + Part 9 checkout) ----
    // Privileged initiate/transition go through src/util/api.js POST helpers to
    // /api/*-privileged (engine + Stripe). transition() here is the
    // non-privileged path (confirm-payment) used by CheckoutPage.duck.
    transactions: {
      query: params => request('/api/transactions/query', { params }),
      show: params => request('/api/transactions/show', { params }),
      transition: (bodyParams, queryParams) =>
        request('/api/transition', { method: 'POST', body: { bodyParams, queryParams } }),
      skeepersSend: params =>
        request('/api/admin/transactions/skeepers-send', { method: 'POST', body: params }),
      // Non-privileged initiate (inquiry process) not migrated yet.
      initiate: reject('transactions.initiate'),
      initiateSpeculative: reject('transactions.initiateSpeculative'),
      transitionSpeculative: reject('transactions.transitionSpeculative'),
    },

    // ---- Auth (Part 5) ----
    login: ({ username, password } = {}) =>
      request('/api/auth/local/login', { method: 'POST', body: { email: username, password } }),
    logout: () => request('/api/auth/local/logout', { method: 'POST', body: {} }),
    authInfo: () =>
      request('/api/me')
        .then(() => ({ isAnonymous: false, grantType: 'refresh_token' }))
        .catch(() => ({ isAnonymous: true, grantType: 'client_credentials' })),
    passwordReset: {
      request: params =>
        request('/api/auth/local/password/reset-request', { method: 'POST', body: params }),
      // Sharetribe SDK shape: { email, passwordResetToken, newPassword }.
      // Endpoint body: { token, password }.
      reset: ({ passwordResetToken, newPassword, token, password } = {}) =>
        request('/api/auth/local/password/reset-confirm', {
          method: 'POST',
          body: { token: token || passwordResetToken, password: password || newPassword },
        }),
    },

    // ---- Hosted config/assets (Part 9 config step) ----
    // Served from the repo snapshot via /api/assets. `alias`/`version` are sent
    // for parity but the endpoint always returns the latest snapshot.
    assetsByAlias: ({ paths, alias } = {}) => request('/api/assets', { params: { paths, alias } }),
    assetByAlias: ({ path, alias } = {}) => request('/api/assets', { params: { path, alias } }),
    assetsByVersion: ({ paths, version } = {}) =>
      request('/api/assets', { params: { paths, version } }),
    assetByVersion: ({ path, version } = {}) =>
      request('/api/assets', { params: { path, version } }),

    ...buildNotMigrated(),
  };
  return sdk;
};
